/**
 * Notification Metrics Tracker
 *
 * Tracks notification interactions for A/B testing and analytics.
 * Integrates with Web Vitals for performance monitoring.
 *
 * Metrics tracked:
 * - Toast view count by variant
 * - Dismiss rate (manual vs auto-dismiss)
 * - Action button click rate
 * - Average time-to-dismiss
 * - Inline feedback usage
 * - Haptic feedback triggers
 *
 * @version 1.0.0
 */

import type { ToastVariant } from '@shared/store/toastStore';

/**
 * Notification metric types
 */
export type NotificationMetricType =
  | 'toast_view'
  | 'toast_dismiss_manual'
  | 'toast_dismiss_auto'
  | 'toast_action_click'
  | 'inline_feedback_trigger'
  | 'haptic_feedback_trigger'
  | 'notification_group_count';

/**
 * Notification metric data
 */
export interface NotificationMetric {
  /** Metric type */
  type: NotificationMetricType;
  /** Toast variant (if applicable) */
  variant?: ToastVariant;
  /** Metric value (count, duration, etc.) */
  value: number;
  /** Timestamp when metric was recorded */
  timestamp: number;
  /** Additional context */
  metadata?: Record<string, any>;
}

/**
 * Aggregated notification metrics
 */
export interface NotificationMetricsAggregate {
  // View counts
  totalViews: number;
  viewsByVariant: Record<ToastVariant, number>;

  // Dismiss counts
  totalDismissals: number;
  manualDismissals: number;
  autoDismissals: number;
  dismissRate: number; // Manual dismiss rate (0-1)

  // Action counts
  actionClicks: number;
  actionClickRate: number; // Click rate per view (0-1)

  // Time metrics
  averageTimeToManualDismiss: number; // in milliseconds
  medianTimeToManualDismiss: number; // in milliseconds

  // Inline feedback
  inlineFeedbackTriggers: number;

  // Haptic feedback
  hapticFeedbackTriggers: number;

  // Grouping
  groupedNotificationCount: number;
  averageGroupSize: number;
}

/**
 * In-memory metrics storage
 */
class NotificationMetricsStore {
  private metrics: NotificationMetric[] = [];
  private toastStartTimes: Map<string, number> = new Map();
  private isEnabled: boolean = false;

  /**
   * Enable metrics collection
   */
  enable(): void {
    this.isEnabled = true;
    console.log('[NotificationMetrics] Metrics collection enabled');
  }

  /**
   * Disable metrics collection
   */
  disable(): void {
    this.isEnabled = false;
    console.log('[NotificationMetrics] Metrics collection disabled');
  }

  /**
   * Check if metrics collection is enabled
   */
  get enabled(): boolean {
    return this.isEnabled;
  }

  /**
   * Record a metric
   */
  record(
    type: NotificationMetricType,
    variant?: ToastVariant,
    value: number = 1,
    metadata?: Record<string, any>
  ): void {
    if (!this.isEnabled) return;

    const metric: NotificationMetric = {
      type,
      variant,
      value,
      timestamp: Date.now(),
      metadata,
    };

    this.metrics.push(metric);

    // Log to console in dev mode
    if (import.meta.env.DEV) {
      console.log('[NotificationMetrics]', metric);
    }
  }

  /**
   * Track toast view
   */
  trackToastView(toastId: string, variant: ToastVariant): void {
    this.record('toast_view', variant);
    this.toastStartTimes.set(toastId, Date.now());
  }

  /**
   * Track manual toast dismiss
   */
  trackToastDismissManual(toastId: string, variant: ToastVariant): void {
    const startTime = this.toastStartTimes.get(toastId);
    const duration = startTime ? Date.now() - startTime : 0;

    this.record('toast_dismiss_manual', variant, duration);
    this.toastStartTimes.delete(toastId);
  }

  /**
   * Track auto toast dismiss
   */
  trackToastDismissAuto(toastId: string, variant: ToastVariant): void {
    this.record('toast_dismiss_auto', variant);
    this.toastStartTimes.delete(toastId);
  }

  /**
   * Track toast action click
   */
  trackToastActionClick(variant: ToastVariant): void {
    this.record('toast_action_click', variant);
  }

  /**
   * Track inline feedback trigger
   */
  trackInlineFeedback(feedbackType: string): void {
    this.record('inline_feedback_trigger', undefined, 1, { feedbackType });
  }

  /**
   * Track haptic feedback trigger
   */
  trackHapticFeedback(intensity: string): void {
    this.record('haptic_feedback_trigger', undefined, 1, { intensity });
  }

  /**
   * Track grouped notification
   */
  trackNotificationGroup(count: number): void {
    this.record('notification_group_count', undefined, count);
  }

  /**
   * Get all metrics
   */
  getMetrics(): NotificationMetric[] {
    return [...this.metrics];
  }

  /**
   * Get aggregated metrics
   */
  getAggregate(): NotificationMetricsAggregate {
    const metrics = this.metrics;

    // View counts
    const viewMetrics = metrics.filter((m) => m.type === 'toast_view');
    const totalViews = viewMetrics.length;
    const viewsByVariant: Record<ToastVariant, number> = {
      success: 0,
      error: 0,
      warning: 0,
      info: 0,
    };
    viewMetrics.forEach((m) => {
      if (m.variant) viewsByVariant[m.variant]++;
    });

    // Dismiss counts
    const manualDismissMetrics = metrics.filter((m) => m.type === 'toast_dismiss_manual');
    const autoDismissMetrics = metrics.filter((m) => m.type === 'toast_dismiss_auto');
    const manualDismissals = manualDismissMetrics.length;
    const autoDismissals = autoDismissMetrics.length;
    const totalDismissals = manualDismissals + autoDismissals;
    const dismissRate = totalDismissals > 0 ? manualDismissals / totalDismissals : 0;

    // Action clicks
    const actionClicks = metrics.filter((m) => m.type === 'toast_action_click').length;
    const actionClickRate = totalViews > 0 ? actionClicks / totalViews : 0;

    // Time to dismiss
    const dismissTimes = manualDismissMetrics.map((m) => m.value).sort((a, b) => a - b);
    const averageTimeToManualDismiss =
      dismissTimes.length > 0
        ? dismissTimes.reduce((sum, val) => sum + val, 0) / dismissTimes.length
        : 0;
    const medianTimeToManualDismiss =
      dismissTimes.length > 0
        ? dismissTimes[Math.floor(dismissTimes.length / 2)]
        : 0;

    // Inline feedback
    const inlineFeedbackTriggers = metrics.filter(
      (m) => m.type === 'inline_feedback_trigger'
    ).length;

    // Haptic feedback
    const hapticFeedbackTriggers = metrics.filter(
      (m) => m.type === 'haptic_feedback_trigger'
    ).length;

    // Grouping
    const groupMetrics = metrics.filter((m) => m.type === 'notification_group_count');
    const groupedNotificationCount = groupMetrics.length;
    const totalGroupSize = groupMetrics.reduce((sum, m) => sum + m.value, 0);
    const averageGroupSize =
      groupedNotificationCount > 0 ? totalGroupSize / groupedNotificationCount : 0;

    return {
      totalViews,
      viewsByVariant,
      totalDismissals,
      manualDismissals,
      autoDismissals,
      dismissRate,
      actionClicks,
      actionClickRate,
      averageTimeToManualDismiss,
      medianTimeToManualDismiss,
      inlineFeedbackTriggers,
      hapticFeedbackTriggers,
      groupedNotificationCount,
      averageGroupSize,
    };
  }

  /**
   * Export metrics to console
   */
  export(): void {
    const aggregate = this.getAggregate();
    console.group('[NotificationMetrics] Aggregate Report');
    console.log('Total Views:', aggregate.totalViews);
    console.log('Views by Variant:', aggregate.viewsByVariant);
    console.log('Manual Dismiss Rate:', `${(aggregate.dismissRate * 100).toFixed(1)}%`);
    console.log('Action Click Rate:', `${(aggregate.actionClickRate * 100).toFixed(1)}%`);
    console.log('Avg Time to Dismiss:', `${aggregate.averageTimeToManualDismiss.toFixed(0)}ms`);
    console.log('Inline Feedback Triggers:', aggregate.inlineFeedbackTriggers);
    console.log('Haptic Feedback Triggers:', aggregate.hapticFeedbackTriggers);
    console.log('Grouped Notifications:', aggregate.groupedNotificationCount);
    console.log('Avg Group Size:', aggregate.averageGroupSize.toFixed(1));
    console.groupEnd();
  }

  /**
   * Export metrics as JSON
   */
  exportJSON(): string {
    const aggregate = this.getAggregate();
    return JSON.stringify(
      {
        aggregate,
        rawMetrics: this.metrics,
      },
      null,
      2
    );
  }

  /**
   * Clear all metrics
   */
  clear(): void {
    this.metrics = [];
    this.toastStartTimes.clear();
    console.log('[NotificationMetrics] Metrics cleared');
  }
}

/**
 * Global metrics store instance
 */
export const notificationMetrics = new NotificationMetricsStore();

/**
 * Enable metrics collection (call once at app startup)
 */
export function enableNotificationMetrics(): void {
  notificationMetrics.enable();
}

/**
 * Disable metrics collection
 */
export function disableNotificationMetrics(): void {
  notificationMetrics.disable();
}

/**
 * Hook to enable/disable metrics based on feature flag
 */
export function useNotificationMetrics(): void {
  // This will be implemented when we integrate with feature flags
  // For now, metrics are enabled in dev mode by default
  if (import.meta.env.DEV) {
    notificationMetrics.enable();
  }
}
